import { ChevronLeft, ChevronRight, Download, Eye } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPayroll } from '../../../api/employee.api';
import styles from '../../../assets/scss/pages/EmployeeList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';

function fmtNum(v) {
    return Math.floor(Number(v) || 0).toLocaleString('vi-VN');
}

function fmtMonth(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function displayMonth(date) {
    return date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' });
}

function safeVal(v) {
    return v === 1 ? 0 : Number(v) || 0;
}

export default function SalaryTable() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canView = hasPermission(PERMISSIONS.SALARY.VIEW);
    const canExport = hasPermission(PERMISSIONS.SALARY.EXPORT);

    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date());
    const [toastMsg, setToastMsg] = useState(null);

    const showToast = useCallback((type, text, duration = 1500) => {
        setToastMsg({ type, text });
        setTimeout(() => setToastMsg(null), duration);
    }, []);

    const fetchData = useCallback(async () => {
        if (!canView) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const { data } = await getAllPayroll({ month: fmtMonth(selectedMonth) });
            setEmployees(data.earnings || []);
        } catch {
            setEmployees([]);
        } finally {
            setLoading(false);
        }
    }, [canView, selectedMonth]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const changeMonth = (delta) => {
        setSelectedMonth((prev) => {
            const d = new Date(prev);
            d.setMonth(d.getMonth() + delta);
            return d;
        });
    };

    const totalSalary = employees.reduce(
        (sum, emp) => sum + safeVal(emp.baseSalary) + safeVal(emp.allowance) + (Number(emp.commission) || 0),
        0,
    );

    const handleExport = async () => {
        if (!canExport) return;
        try {
            const ExcelJS = await import('exceljs');
            const { saveAs } = await import('file-saver');
            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'Navo Sổ Tiệm';
            const ws = workbook.addWorksheet('Bảng lương');

            const fontDefault = { name: 'Times New Roman', size: 11 };
            const fontBold = { name: 'Times New Roman', size: 11, bold: true };
            const borderThin = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' },
            };
            const alignCenter = { vertical: 'middle', horizontal: 'center' };
            const alignRight = { vertical: 'middle', horizontal: 'right' };
            const alignLeft = { vertical: 'middle', horizontal: 'left', indent: 1 };

            // Column widths
            ws.columns = [
                { width: 6 },
                { width: 28 },
                { width: 18 },
                { width: 16 },
                { width: 18 },
                { width: 16 },
                { width: 20 },
            ];

            // Row 1: Title
            ws.mergeCells('A1:G1');
            const titleCell = ws.getCell('A1');
            titleCell.value = 'BẢNG LƯƠNG NHÂN VIÊN';
            titleCell.font = { name: 'Times New Roman', size: 16, bold: true };
            titleCell.alignment = alignCenter;
            ws.getRow(1).height = 32;

            // Row 2: Period
            ws.mergeCells('A2:G2');
            const periodCell = ws.getCell('A2');
            periodCell.value = `Tháng: ${displayMonth(selectedMonth)}`;
            periodCell.font = { name: 'Times New Roman', size: 11, italic: true, color: { argb: 'FF666666' } };
            periodCell.alignment = alignCenter;
            ws.getRow(2).height = 22;

            // Row 3: blank
            ws.getRow(3).height = 8;

            // Row 4: Header
            const headers = [
                'STT',
                'Tên nhân viên',
                'Lương cơ bản',
                'Phụ cấp',
                'Doanh thu',
                'Hoa hồng',
                'Tổng thu nhập',
            ];
            const headerRow = ws.getRow(4);
            headers.forEach((h, i) => {
                const cell = headerRow.getCell(i + 1);
                cell.value = h;
                cell.font = { name: 'Times New Roman', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
                cell.alignment = alignCenter;
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0098F6' } };
                cell.border = borderThin;
            });
            headerRow.height = 28;

            // Data rows
            employees.forEach((emp, idx) => {
                const total = safeVal(emp.baseSalary) + safeVal(emp.allowance) + (Number(emp.commission) || 0);
                const row = ws.getRow(5 + idx);
                const vals = [
                    idx + 1,
                    emp.name,
                    Math.round(safeVal(emp.baseSalary)),
                    Math.round(safeVal(emp.allowance)),
                    Math.round(Number(emp.totalEarnings) || 0),
                    Math.round(Number(emp.commission) || 0),
                    Math.round(total),
                ];
                vals.forEach((v, i) => {
                    const cell = row.getCell(i + 1);
                    cell.value = v;
                    cell.font = i === 6 ? fontBold : fontDefault;
                    cell.alignment = i === 0 ? alignCenter : i === 1 ? alignLeft : alignRight;
                    cell.border = borderThin;
                    if (i >= 2) cell.numFmt = '#,##0';
                    if (idx % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF9FAFB' } };
                });
                row.height = 22;
            });

            // Total row
            const totalRow = ws.getRow(5 + employees.length);
            ws.mergeCells(5 + employees.length, 1, 5 + employees.length, 2);
            totalRow.getCell(1).value = 'TỔNG LƯƠNG';
            totalRow.getCell(1).font = { name: 'Times New Roman', size: 12, bold: true };
            totalRow.getCell(1).alignment = alignCenter;
            for (let c = 1; c <= 7; c++) {
                totalRow.getCell(c).border = borderThin;
                totalRow.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8F5E9' } };
            }
            totalRow.getCell(7).value = Math.round(totalSalary);
            totalRow.getCell(7).font = { name: 'Times New Roman', size: 12, bold: true, color: { argb: 'FF22C55E' } };
            totalRow.getCell(7).alignment = alignRight;
            totalRow.getCell(7).numFmt = '#,##0';
            totalRow.height = 26;

            // Employee count
            const noteRow = ws.getRow(6 + employees.length);
            ws.mergeCells(6 + employees.length, 1, 6 + employees.length, 3);
            noteRow.getCell(1).value = `Tổng số nhân viên: ${employees.length}`;
            noteRow.getCell(1).font = { name: 'Times New Roman', size: 11, italic: true };
            noteRow.getCell(1).alignment = alignLeft;

            // Print setup
            ws.pageSetup = {
                paperSize: 9,
                orientation: 'landscape',
                fitToPage: true,
                fitToWidth: 1,
                fitToHeight: 0,
                margins: { left: 0.5, right: 0.5, top: 0.6, bottom: 0.6, header: 0.3, footer: 0.3 },
            };
            ws.views = [{ state: 'frozen', ySplit: 4 }];

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            saveAs(blob, `BangLuong_${fmtMonth(selectedMonth)}.xlsx`);
            showToast('success', 'Xuất Excel thành công');
        } catch (err) {
            console.error('Export error:', err);
            showToast('error', 'Xuất Excel thất bại', 2500);
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Quản lý nhân sự</h1>
                    <p className={styles.subtitle}>Bảng lương {displayMonth(selectedMonth)}</p>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    {/* Month selector */}
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            background: '#fff',
                            border: '1.5px solid #ECEEF2',
                            borderRadius: 12,
                            padding: '0 6px',
                            height: 46,
                        }}
                    >
                        <button
                            style={{
                                width: 36,
                                height: 36,
                                border: 'none',
                                borderRadius: 8,
                                background: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#6B7280',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#F0F9FF';
                                e.currentTarget.style.color = '#0098F6';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.color = '#6B7280';
                            }}
                            onClick={() => changeMonth(-1)}
                        >
                            <ChevronLeft size={20} />
                        </button>
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 700,
                                minWidth: 160,
                                textAlign: 'center',
                                color: '#1A1A1A',
                            }}
                        >
                            {displayMonth(selectedMonth)}
                        </span>
                        <button
                            style={{
                                width: 36,
                                height: 36,
                                border: 'none',
                                borderRadius: 8,
                                background: 'none',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#6B7280',
                                transition: 'all 0.15s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#F0F9FF';
                                e.currentTarget.style.color = '#0098F6';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'none';
                                e.currentTarget.style.color = '#6B7280';
                            }}
                            onClick={() => changeMonth(1)}
                        >
                            <ChevronRight size={20} />
                        </button>
                    </div>

                    {canExport && (
                        <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={handleExport}>
                            <Download /> Xuất Excel
                        </button>
                    )}
                </div>
            </div>

            {/* Summary */}
            {!loading && employees.length > 0 && (
                <div className={styles.kpis} style={{ gridTemplateColumns: '1fr' }}>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>Tổng lương tháng {selectedMonth.getMonth() + 1}</span>
                            <span className={styles.kpiValue}>{fmtNum(totalSalary)} đ</span>
                        </div>
                    </div>
                </div>
            )}

            {/* Tip */}
            {!loading && employees.length > 0 && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        padding: '12px 18px',
                        background: 'linear-gradient(90deg, #E6F4FF 0%, #F0F9FF 100%)',
                        border: '1.5px solid #0098F6',
                        borderRadius: 10,
                        marginBottom: 16,
                        cursor: 'default',
                    }}
                >
                    <span style={{ fontSize: 20 }}>💡</span>
                    <span style={{ fontSize: 14, color: '#0068CC', fontWeight: 500, lineHeight: 1.5 }}>
                        *Chú ý: Nhấn vào <strong>tên nhân viên</strong> để xem chi tiết hoa hồng từng đơn và KPI trong
                        tháng
                    </span>
                </div>
            )}

            {/* Table */}
            <div className={styles.tableWrapper}>
                {loading ? (
                    <div className={styles.loading}>
                        <div className={styles.spinner} /> Đang tải...
                    </div>
                ) : employees.length === 0 ? (
                    <div className={styles.empty}>Không có dữ liệu lương tháng này</div>
                ) : (
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th style={{ width: 45, textAlign: 'center' }}>STT</th>
                                <th>Nhân viên</th>
                                <th style={{ textAlign: 'right' }}>Lương cơ bản</th>
                                <th style={{ textAlign: 'right' }}>Phụ cấp</th>
                                <th style={{ textAlign: 'right' }}>Doanh thu</th>
                                <th style={{ textAlign: 'right' }}>Hoa hồng</th>
                                <th style={{ textAlign: 'right' }}>Tổng thu nhập</th>
                                {canExport && <th style={{ width: 80, textAlign: 'center' }}>Chi tiết</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {employees.map((emp, idx) => {
                                const total =
                                    safeVal(emp.baseSalary) + safeVal(emp.allowance) + (Number(emp.commission) || 0);
                                return (
                                    <tr
                                        key={emp.employeeId || idx}
                                        className={styles.rowClickable}
                                        onClick={() =>
                                            canExport &&
                                            navigate(
                                                `/admin/salary/${emp.employeeId}?month=${fmtMonth(selectedMonth)}&name=${encodeURIComponent(emp.name)}`,
                                            )
                                        }
                                    >
                                        <td style={{ textAlign: 'center', color: '#6B7280' }}>{idx + 1}</td>
                                        <td style={{ fontWeight: 500 }}>{emp.name}</td>
                                        <td style={{ textAlign: 'right' }}>{fmtNum(safeVal(emp.baseSalary))}</td>
                                        <td style={{ textAlign: 'right' }}>{fmtNum(safeVal(emp.allowance))}</td>
                                        <td style={{ textAlign: 'right', color: '#0098F6' }}>
                                            {fmtNum(emp.totalEarnings)}
                                        </td>
                                        <td style={{ textAlign: 'right', color: '#F59E0B' }}>
                                            {fmtNum(emp.commission)}
                                        </td>
                                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#22C55E' }}>
                                            {fmtNum(total)}
                                        </td>
                                        {canExport && (
                                            <td style={{ textAlign: 'center' }}>
                                                <Eye size={16} style={{ color: '#0098F6' }} />
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {toastMsg && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: 2000,
                        background: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderRadius: 20,
                            padding: '40px 48px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 16,
                            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        }}
                    >
                        <div
                            style={{
                                width: 60,
                                height: 60,
                                borderRadius: '50%',
                                background: toastMsg.type === 'success' ? '#22C55E' : '#EF4444',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 28,
                                fontWeight: 700,
                            }}
                        >
                            {toastMsg.type === 'success' ? '✓' : '✕'}
                        </div>
                        <span style={{ fontSize: 16, fontWeight: 600, color: '#1A1A1A' }}>{toastMsg.text}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
